import React, { useState, useEffect, lazy, Suspense } from 'react';
import { AnimatePresence } from 'framer-motion';
import Layout from './components/Layout/Layout';
import { TabType } from './types';
import { WagmiProvider } from './contexts/WagmiProvider';
import { NetworkProvider } from './contexts/NetworkContext';
import { PriceProvider } from './contexts/PriceContext';

// Lazy load pages for better initial load performance
const Home = lazy(() => import('./pages/Home'));
const SwapWithProviders = lazy(() => import('./pages/SwapWithProviders'));
const Bridge = lazy(() => import('./pages/Bridge'));
const Polymarket = lazy(() => import('./pages/Polymarket'));
const Lend = lazy(() => import('./pages/Lend'));
const Watcher = lazy(() => import('./pages/Watcher'));
const Security = lazy(() => import('./pages/Security'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Docs = lazy(() => import('./pages/Docs'));

// Loading skeleton for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
      <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-purple-500/50 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
    </div>
  </div>
);

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('home');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top when tab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  // Listen for navigate-to-terms events (from trading modals, etc.)
  useEffect(() => {
    const handleNavigateToTerms = () => {
      setActiveTab('terms');
    };
    
    window.addEventListener('navigate-to-terms', handleNavigateToTerms);
    return () => {
      window.removeEventListener('navigate-to-terms', handleNavigateToTerms);
    };
  }, []);

  const renderContent = () => {
    const content = (() => {
      switch (activeTab) {
        case 'home':
          return <Home onNavigate={setActiveTab} />;
        case 'swap':
          return <SwapWithProviders />;
        case 'bridge':
          return <Bridge />;
        case 'polymarket':
          return <Polymarket />;
        case 'lend':
          return <Lend />;
        case 'watcher':
          return <Watcher />;
        case 'security':
          return <Security />;
        case 'terms':
          return <TermsOfService onBack={() => setActiveTab('home')} />;
        case 'cookies':
          return <CookiePolicy onBack={() => setActiveTab('home')} />;
        case 'docs':
          return <Docs onBack={() => setActiveTab('home')} />;
        default:
          return <Home onNavigate={setActiveTab} />;
      }
    })();
    
    return <Suspense fallback={<PageLoader />}>{content}</Suspense>;
  };

  return (
    <WagmiProvider>
      <PriceProvider>
        <NetworkProvider>
          <div className="App">
            <Layout activeTab={activeTab} onTabChange={setActiveTab}>
              <AnimatePresence mode="wait">
                {renderContent()}
              </AnimatePresence>
            </Layout>
          </div>
        </NetworkProvider>
      </PriceProvider>
    </WagmiProvider>
  );
}

export default App;








